import { OfferErrorCode, ValidationErrorCode } from '@today/types'
import { OfferWizardStepId } from '@/pages/Offers/offer-wizard-steps'

/** How Step 4 explains a refused PATCH or send (TO-125 BR-8); the catalog key `send.refusal.<value>` words it. */
export enum SendRefusalKind {
	CONSENT_MISSING = 'consentMissing',
	INCOMPLETE = 'incomplete',
	EXPIRED = 'expired',
	UNREACHABLE = 'unreachable',
	CHANNEL_NOT_ENABLED = 'channelNotEnabled',
	CONFLICT = 'conflict',
	GENERIC = 'generic',
}

/** A refusal as Step 4 shows it: its kind, the channels it names and the steps to go back to. */
export interface SendRefusal {
	kind: SendRefusalKind
	channels: string[]
	steps: OfferWizardStepId[]
}

interface ApiErrorBody {
	error?: { code?: string; details?: { channels?: string[]; fields?: string[]; field?: string } }
}

/** The wizard step that completes each missing field of an `OFFER_INCOMPLETE`. */
const STEP_OF_FIELD: Record<string, OfferWizardStepId> = {
	productIds: OfferWizardStepId.PRODUCTS,
	cta: OfferWizardStepId.PREVIEW,
}

const refusal = (kind: SendRefusalKind, channels: string[] = [], steps: OfferWizardStepId[] = []): SendRefusal => ({
	kind,
	channels,
	steps,
})

/** The kind of each refusal code whose message is fixed (the 409s are handled first). */
const KIND_OF_CODE: Readonly<Record<string, SendRefusalKind>> = {
	[ValidationErrorCode.OFFER_CONSENT_MISSING]: SendRefusalKind.CONSENT_MISSING,
	[ValidationErrorCode.OFFER_INCOMPLETE]: SendRefusalKind.INCOMPLETE,
	[ValidationErrorCode.OFFER_EXPIRED_BEFORE_SEND]: SendRefusalKind.EXPIRED,
	// The only VALIDATION_ERROR Step 4 can cause is an out-of-bounds expiry on the PATCH.
	[ValidationErrorCode.VALIDATION_ERROR]: SendRefusalKind.EXPIRED,
	[ValidationErrorCode.OFFER_RECIPIENT_UNREACHABLE]: SendRefusalKind.UNREACHABLE,
	[ValidationErrorCode.OFFER_CHANNEL_NOT_ENABLED]: SendRefusalKind.CHANNEL_NOT_ENABLED,
	[OfferErrorCode.OFFER_NOT_EDITABLE]: SendRefusalKind.CONFLICT,
}

/** The kinds whose message names the refused channels. */
const NAMES_CHANNELS = new Set([
	SendRefusalKind.CONSENT_MISSING,
	SendRefusalKind.UNREACHABLE,
	SendRefusalKind.CHANNEL_NOT_ENABLED,
])

/** Map a refused `PATCH /offers/{id}` or `POST /offers/{id}/send` to what Step 4 shows (TO-125 REQ-107). */
export function sendRefusalOf(error: unknown): SendRefusal {
	const response = (error as { response?: { status?: number; data?: ApiErrorBody } } | null)?.response
	if (!response) return refusal(SendRefusalKind.GENERIC)
	if (response.status === 409) return refusal(SendRefusalKind.CONFLICT)
	const { code, details } = response.data?.error ?? {}
	const kind = (code && KIND_OF_CODE[code]) || SendRefusalKind.GENERIC
	if (kind === SendRefusalKind.INCOMPLETE) {
		const steps = (details?.fields ?? []).flatMap((field) => (STEP_OF_FIELD[field] ? [STEP_OF_FIELD[field]] : []))
		return refusal(kind, [], steps)
	}
	return refusal(kind, NAMES_CHANNELS.has(kind) ? (details?.channels ?? []) : [])
}
